import type { ChatMessageAdmin } from '@pin/schemas/socket';
import type { ChatMessage } from './history';

/**
 * Whether a user is muted, as the feed's messages tell it. A mute is marked
 * on the user's last message before it, and one that runs out clears only
 * that message, not the ones the user sent while muted. Messages loaded as
 * history carry no mute state. Null when nothing tells: no message of the
 * user is in the feed, or only muted messages are, whose mute may since
 * have run out on a message that has left the feed.
 */
export const isUserMuted = (
  messages: ChatMessage[],
  userId: string
): boolean | null => {
  const own = messages.filter((m) => m.user.id === userId);
  if (own.length === 0) {
    return null;
  }
  const marked = own.filter((m) => m.muted || m.unmuted).at(-1);
  if (marked != null) {
    return marked.muted;
  }
  return own.some((m) => m.mutedUser) ? null : false;
};

/**
 * The user as a message of theirs has it, with the account a child profile
 * acts through. A user opened from the overlay has only id and name.
 * client-api bans and mutes the account, so a failed lookup is passed on
 * rather than acting on the profile alone. With no message found, the user
 * is returned as given if it carries the account, else null: it may be a
 * child profile whose account is not known.
 */
export const userWithAccount = async (
  user: ChatMessageAdmin['user'],
  messages: ChatMessageAdmin[],
  fetchLatest: (userId: string) => Promise<ChatMessageAdmin[]>
) => {
  const message =
    messages.filter((m) => m.user.id === user.id).at(-1) ??
    (await fetchLatest(user.id)).at(0);
  if (message != null) {
    return message.user;
  }
  return user.viaUserId != null ? user : null;
};
